import { useMemo } from 'react'
import { Tooltip } from '@/components/ui'
import {
  granularityFor,
  money,
  periodKey,
  periodLabel,
} from '../utils/history-periods'
import type { OpenOrderRow } from '../api/types'

/**
 * Summary for the Order History view: what a customer bought, when, and
 * what it earned.
 *
 * The Open view's summary is a state distribution, which says nothing
 * useful about the past — "how many of these closed orders are LATE" is
 * not a question anyone asks of history. This renders the two that are
 * actually asked: revenue over time, and product mix over time.
 *
 * Charting is hand-rolled; there is no charting library in this repo
 * (see margins/MarginSummaryChart.tsx and
 * product-customer-pricing/Sparkline.tsx, both the same). Module-local
 * per ops/CLAUDE.md's ">=2 consumers" rule for shared primitives.
 *
 * Per dataviz guidance:
 *  - Change-over-time reads left to right, so periods are COLUMNS here,
 *    not the horizontal bars used elsewhere in the app.
 *  - ONE series (revenue). Margin is a ratio and revenue is dollars;
 *    putting both on one axis would be a dual-axis chart. Margin rides
 *    as a direct label instead.
 *  - One series means no legend (the heading names it) and no
 *    categorical palette. `--color-accent` was validated standalone
 *    against both surfaces (light #0055a5 on #ffffff, dark #3d80c7 on
 *    #1a1d22) — all six checks pass. Blue and purple, the only two
 *    non-status ramps this design system has, FAIL separation
 *    (normal-vision dE 12.0), which is the other reason for one series.
 *  - Values wear text tokens, never the series color.
 *  - The product x period table below doubles as the chart's table view.
 */

/** Products shown individually before the tail folds into "Other". */
const TOP_PRODUCTS = 12

interface Bucket {
  key: string
  revenue: number
  margin: number
  orders: Set<string>
}

export function OrderHistorySummary({
  rows,
  dateFrom,
  dateTo,
}: {
  rows: OpenOrderRow[]
  dateFrom: string
  dateTo: string
}) {
  const g = granularityFor(dateFrom, dateTo)

  const { periods, byPeriod, products, cell, grandTotal } = useMemo(() => {
    const bucketMap = new Map<string, Bucket>()
    const productTotals = new Map<string, number>()
    const cellMap = new Map<string, number>()
    let total = 0

    for (const r of rows) {
      if (!r.confirmation_date) continue
      const key = periodKey(r.confirmation_date, g)

      let b = bucketMap.get(key)
      if (!b) {
        b = { key, revenue: 0, margin: 0, orders: new Set() }
        bucketMap.set(key, b)
      }
      b.revenue += r.revenue
      b.margin += r.est_margin
      b.orders.add(r.so_number)

      const name = r.product_name || '-'
      productTotals.set(name, (productTotals.get(name) ?? 0) + r.revenue)
      const ck = `${name} ${key}`
      cellMap.set(ck, (cellMap.get(ck) ?? 0) + r.revenue)
      total += r.revenue
    }

    return {
      periods: [...bucketMap.keys()].sort(),
      byPeriod: bucketMap,
      products: [...productTotals.entries()].sort((a, b) => b[1] - a[1]),
      cell: cellMap,
      grandTotal: total,
    }
  }, [rows, g])

  if (periods.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-tertiary">
        No orders in this window.
      </p>
    )
  }

  const maxRevenue = Math.max(...periods.map((p) => byPeriod.get(p)!.revenue), 1)
  const head = products.slice(0, TOP_PRODUCTS)
  const tail = products.slice(TOP_PRODUCTS)

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-8">
      <section>
        <h2 className="text-lg font-semibold text-primary">Revenue by {g}</h2>
        <p className="mb-4 text-xs text-tertiary">
          {money(grandTotal)} across {rows.length.toLocaleString()} lines.
          Margin is estimated from standard cost at order time — see Margins
          for actual COGS.
        </p>

        <div
          // items-stretch (not items-end): each column must be full
          // height for the bar's percentage height to resolve against
          // something. The column's own justify-end drops the bar to the
          // baseline. With items-end the columns shrink-wrap and every
          // bar computes to 0.
          className="flex h-56 items-stretch gap-[2px]"
          role="img"
          aria-label={`Revenue by ${g}, ${periodLabel(periods[0]!, g)} to ${periodLabel(
            periods[periods.length - 1]!,
            g,
          )}`}
        >
          {periods.map((p) => {
            const b = byPeriod.get(p)!
            const pct = b.revenue ? (b.margin / b.revenue) * 100 : null
            return (
              <Tooltip
                key={p}
                label={
                  <span className="whitespace-nowrap">
                    {periodLabel(p, g)} &middot; {money(b.revenue)} &middot;{' '}
                    {b.orders.size} order{b.orders.size === 1 ? '' : 's'}
                    {pct !== null ? ` (${pct.toFixed(1)}% est. margin)` : ''}
                  </span>
                }
              >
                <div className="flex min-w-0 flex-1 cursor-default flex-col items-center justify-end gap-1">
                  <span className="text-2xs tabular-nums text-secondary">
                    {pct !== null ? `${pct.toFixed(0)}%` : ''}
                  </span>
                  <span
                    // rounded-t-sm == 4px: the dataviz mark spec's rounded
                    // data-end, with the baseline end left square.
                    className="w-full rounded-t-sm bg-accent"
                    style={{
                      height: `${Math.max((b.revenue / maxRevenue) * 100, 1)}%`,
                    }}
                  />
                </div>
              </Tooltip>
            )
          })}
        </div>
        <div className="flex gap-[2px]">
          {periods.map((p) => (
            <span
              key={p}
              className="min-w-0 flex-1 truncate pt-1.5 text-center text-2xs text-tertiary"
            >
              {periodLabel(p, g)}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold text-primary">
          Products by {g}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border-default">
                <th className="sticky left-0 bg-canvas py-1.5 pr-3 text-left font-medium text-tertiary">
                  Product
                </th>
                {periods.map((p) => (
                  <th
                    key={p}
                    className="px-2 py-1.5 text-right font-medium text-tertiary"
                  >
                    {periodLabel(p, g)}
                  </th>
                ))}
                <th className="py-1.5 pl-3 text-right font-medium text-tertiary">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {head.map(([name, tot]) => (
                <tr key={name} className="border-b border-border-subtle">
                  <td className="sticky left-0 max-w-64 truncate bg-canvas py-1.5 pr-3 text-primary">
                    {name}
                  </td>
                  {periods.map((p) => {
                    const v = cell.get(`${name} ${p}`)
                    return (
                      <td
                        key={p}
                        className="px-2 py-1.5 text-right tabular-nums text-secondary"
                      >
                        {v ? money(v) : <span className="text-tertiary">-</span>}
                      </td>
                    )
                  })}
                  <td className="py-1.5 pl-3 text-right font-medium tabular-nums text-primary">
                    {money(tot)}
                  </td>
                </tr>
              ))}
              {tail.length > 0 ? (
                <tr className="border-b border-border-subtle">
                  <td className="sticky left-0 bg-canvas py-1.5 pr-3 italic text-secondary">
                    Other ({tail.length} product{tail.length === 1 ? '' : 's'})
                  </td>
                  {periods.map((p) => {
                    const v = tail.reduce(
                      (sum, [n]) => sum + (cell.get(`${n} ${p}`) ?? 0),
                      0,
                    )
                    return (
                      <td
                        key={p}
                        className="px-2 py-1.5 text-right tabular-nums text-secondary"
                      >
                        {v ? money(v) : <span className="text-tertiary">-</span>}
                      </td>
                    )
                  })}
                  <td className="py-1.5 pl-3 text-right font-medium tabular-nums text-primary">
                    {money(tail.reduce((s, [, t]) => s + t, 0))}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
